import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Reveal, SectionLabel } from "./Reveal";

const SERVICES = [
  { id: "preventivo", label: "Preventivo d'acquisto" },
  { id: "permuta", label: "Valutazione usato / permuta" },
  { id: "riparazione", label: "Riparazione" },
  { id: "info", label: "Informazioni generali" },
];
const EMPTY = { name: "", email: "", phone: "", service: "preventivo", message: "" };
const field = "w-full rounded-2xl border border-fg/10 bg-[var(--ink-2)] px-5 py-4 text-fg outline-none transition-colors placeholder:text-mist focus:border-[var(--volt)]/60";

export const RequestForm = () => {
  const { items } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [attach, setAttach] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const on = (e) => {
      setForm((f) => ({ ...f, service: e.detail?.service || f.service }));
      setAttach(!!e.detail?.attachCart);
      setDone(false);
    };
    window.addEventListener("nodo:request", on);
    return () => window.removeEventListener("nodo:request", on);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const cart = attach ? items.map((i) => ({ product_id: i.id, name: i.name, quantity: i.qty, price: i.price })) : [];
      await http.post("/requests", { ...form, items: cart });
      setDone(true);
      setForm(EMPTY);
      toast.success("Richiesta inviata. Ti ricontattiamo a breve.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="richiesta" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8">
      <div className="grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <SectionLabel index="03">Richiesta</SectionLabel>
          <h2 className="mt-5 font-display text-5xl font-semibold leading-[0.9] tracking-tight sm:text-7xl">Parliamone<span className="text-[var(--flare)]">.</span></h2>
          <p className="mt-6 max-w-sm text-fg2">Preventivo, permuta, riparazione o un semplice dubbio: scrivici e ti rispondiamo noi, senza bot.</p>
          <ol className="mt-10 space-y-4 font-mono-ui text-sm text-mist">
            <li><span className="text-[var(--volt)]">01</span> — Compili il modulo</li>
            <li><span className="text-[var(--volt)]">02</span> — Valutiamo la richiesta</li>
            <li><span className="text-[var(--volt)]">03</span> — Ti ricontattiamo con una proposta</li>
          </ol>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <AnimatePresence initial={false}>
            {done ? (
              <motion.div key="ok" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="request-form-success"
                className="flex min-h-[420px] flex-col items-start justify-center rounded-[2rem] border border-[var(--volt)]/30 bg-[var(--ink-2)] p-10">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--volt)] text-[var(--ink)]"><Check className="h-7 w-7" /></span>
                <h3 className="mt-6 font-display text-4xl">Ricevuto.</h3>
                <p className="mt-2 text-fg2">Abbiamo la tua richiesta. Ti scriviamo o chiamiamo appena possibile.</p>
                <button onClick={() => setDone(false)} data-testid="request-form-new" className="mt-8 rounded-full border border-fg/20 px-6 py-3 text-sm hover:bg-fg/5">Invia un'altra richiesta</button>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} data-testid="request-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-4 sm:grid-cols-2">
                <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Nome e cognome" data-testid="request-form-name-input" className={field} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="request-form-email-input" className={field} />
                <input value={form.phone} onChange={set("phone")} placeholder="Telefono (facoltativo)" data-testid="request-form-phone-input" className={field} />
                <select value={form.service} onChange={set("service")} data-testid="request-form-service-select" className={`${field} appearance-none`}>
                  {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <textarea rows={5} value={form.message} onChange={set("message")} placeholder="Raccontaci cosa ti serve (modello, colore, difetto…)" data-testid="request-form-message-input" className={`${field} sm:col-span-2`} />
                {items.length > 0 && (
                  <label className="flex items-center gap-3 text-sm text-fg2 sm:col-span-2">
                    <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="request-form-attach-cart" className="h-4 w-4 accent-[var(--volt)]" />
                    Allega i {items.length} prodotti del carrello alla richiesta
                  </label>
                )}
                <button disabled={sending} data-testid="request-form-submit-button"
                  className="shine flex items-center justify-center gap-2 rounded-full bg-[var(--paper)] px-8 py-4 font-semibold text-[var(--ink)] disabled:opacity-60 active:scale-95 sm:col-span-2 sm:justify-self-start">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Invia richiesta
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};
